import React from 'react';
import { Typography, Box } from '@mui/material';
import {
    ResponsiveContainer,
    PieChart as RechartsPieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';

interface DonutChartData {
    name: string;
    value: number;
    color?: string;
    [key: string]: string | number | undefined;
}

interface DonutChartProps extends ChartHeaderProps {
    data: DonutChartData[];
    colors?: string[];
    height?: number;
    graphHeight?: number;
    showLabels?: boolean;
    showLegend?: boolean;
    innerRadius?: number;
    outerRadius?: number;
    showCenterLabel?: boolean;
    centerLabel?: string;
    onDetailsClick?: (data?: any) => void;
}

const RAD = Math.PI / 180;
const shortName = (s = '', max = 12) => (s.length > max ? `${s.slice(0, max)}...` : s);

const renderLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    name,
    value,
}: any) => {
    const r = outerRadius + 45;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    const anchor = x > cx ? 'start' : 'end';
    const pct = `${((Number(percent) || 0) * 100).toFixed(0)}%`;

    return (
        <text
            x={x}
            y={y}
            textAnchor={anchor}
            dominantBaseline="central"
            fontSize={12}
            fill="#556070"
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        >
            <title>{`${name}: ${value} (${pct})`}</title>
            {`${shortName(name)} ${pct}`}
        </text>
    );
};

// Leader line from the slice to the label (elbow style)
const renderLabelLine = ({ cx, cy, midAngle, outerRadius }: any) => {
    const r1 = outerRadius + 4;
    const r2 = outerRadius + 40;
    const sx = cx + r1 * Math.cos(-midAngle * RAD);
    const sy = cy + r1 * Math.sin(-midAngle * RAD);
    const mx = cx + r2 * Math.cos(-midAngle * RAD);
    const my = cy + r2 * Math.sin(-midAngle * RAD);
    const ex = mx + (mx > cx ? 8 : -8);
    const ey = my;

    return (
        <path
            d={`M${sx},${sy} L${mx},${my} L${ex},${ey}`}
            stroke="#CCD4DD"
            fill="none"
            strokeWidth={1}
            shapeRendering="geometricPrecision"
        />
    );
};

const DonutChart: React.FC<DonutChartProps> = ({
    title,
    description,
    details,
    data,
    colors = ['#8E79FF', '#F9C07D', '#B794F6', '#6EA8FF', '#63D3B1', '#FF8A65', '#4F86F7', '#6CC5D9', '#8BC34A', '#F06292'],
    height = 340,
    graphHeight = 340,
    showLabels = false,
    showLegend = true,
    innerRadius = 80,
    outerRadius = 120,
    showCenterLabel = true,
    centerLabel,
    onDetailsClick
}) => {
    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
    const total = data.reduce((acc, d) => acc + Number(d.value || 0), 0);

    const onPieEnter = (_: any, index: number) => {
        setActiveIndex(index);
    };

    const onPieLeave = () => {
        setActiveIndex(null);
    };

    const handlePieClick = (data: any, index: number) => {
        if (onDetailsClick) {
            onDetailsClick(data);
        }
        console.log('Clicked Donut Chart Segment:');
        console.log('Segment Name:', data.name);
        console.log('Value:', data.value);
        console.log('Percentage:', total ? `${((data.value / total) * 100).toFixed(2)}%` : '0%');
        console.log('Index:', index);
        console.log('Full Data:', data);
    };

    const centerValue = activeIndex !== null ? data[activeIndex]?.value : total;
    const centerLabelText = activeIndex !== null ? data[activeIndex]?.name : (centerLabel || 'Total');

    return (
        <ChartCardWrapper sx={{ height }}>
            <ChartHeader title={title} description={description} details={details} />
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        WebkitTapHighlightColor: 'transparent',
                        userSelect: 'none',
                        '& .recharts-pie-sector': {
                            outline: 'none !important',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                        },
                        '& .recharts-pie-sector:hover': {
                            filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.25)) brightness(1.1)',
                            transform: 'scale(1.05)',
                            transformOrigin: 'center',
                        },
                    }}
                >
                    <ResponsiveContainer width="100%" height={graphHeight}>
                        <RechartsPieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                dataKey="value"
                                innerRadius={innerRadius}
                                outerRadius={outerRadius}
                                startAngle={90}
                                endAngle={-270}
                                minAngle={2}
                                isAnimationActive={false}
                                labelLine={showLabels ? renderLabelLine : false}
                                label={showLabels ? renderLabel : false}
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={onPieEnter}
                                onMouseLeave={onPieLeave}
                                onClick={handlePieClick}
                            >
                                {data.map((entry, index) => {
                                    const isActive = activeIndex === index;
                                    return (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.color || colors[index % colors.length]}
                                            style={{
                                                outline: 'none',
                                                cursor: 'pointer',
                                                transition: 'opacity 0.3s ease',
                                            }}
                                            stroke={isActive ? '#fff' : 'transparent'}
                                            strokeWidth={isActive ? 2 : 0}
                                        />
                                    );
                                })}
                            </Pie>

                            {/* <Tooltip
                                wrapperStyle={{ zIndex: 9999 }}
                                content={(tooltipProps: any) => {
                                    const { active, payload } = tooltipProps || {};
                                    if (!active || !payload || !payload.length) return null;
                                    const entry = payload[0];
                                    const data = entry.payload as DonutChartData;
                                    const fill = (entry.fill as string) || data.color || '#000';
                                    const value = Number(data?.value) || 0;
                                    const pct = total ? `${Math.round((value / total) * 100)}%` : '0%';

                                    return (
                                        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.12)', padding: '8px 12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 10, height: 10, background: fill, borderRadius: 2 }} />
                                                <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>{data?.name || ''}</span>
                                                <span style={{ color: '#6b7280', fontSize: 13 }}>:</span>
                                                <span style={{ color: '#111827', fontSize: 13, fontWeight: 600 }}>{value} ({pct})</span>
                                            </div>
                                        </div>
                                    );
                                }}
                            /> */}
                            {showLegend && (
                                <Legend
                                    verticalAlign="bottom"
                                    align="left"
                                    wrapperStyle={{
                                        paddingTop: 20,
                                        paddingLeft: 4,
                                        paddingRight: 4,
                                        paddingBottom: 4,
                                        fontSize: 10
                                    }}
                                    formatter={(value: string, entry: any) => {
                                        const item = data.find(d => d.name === value);
                                        const itemValue = item?.value || 0;
                                        const pct = total ? Math.round((Number(itemValue) / total) * 100) : 0;
                                        return (
                                            <span
                                                title={`${value}: ${itemValue} (${pct}%)`}
                                                style={{
                                                    verticalAlign: 'middle',
                                                    lineHeight: '20px',
                                                    display: 'inline-block'
                                                }}
                                            >
                                                {shortName(value, 10)}
                                            </span>
                                        );
                                    }}
                                    iconType="square"
                                />
                            )}
                        </RechartsPieChart>
                    </ResponsiveContainer>
                </Box>

                {/* Center label for donut chart */}
                {showCenterLabel && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            pointerEvents: 'none',
                            marginTop: '-30px',
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 300,
                                color: '#111827',
                                transition: 'all 0.3s ease',
                                fontSize: activeIndex !== null ? '2rem' : '1.5rem'
                            }}
                        >
                            {centerValue}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#6b7280',
                                fontSize: 12,
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.3s ease',
                                fontWeight: activeIndex !== null ? 600 : 400
                            }}
                        >
                            {centerLabelText}
                        </Typography>
                    </Box>
                )}
            </Box>
        </ChartCardWrapper>
    );
};

export default DonutChart;
